/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CosmosEnumEdsBackgroundColors } from "@cosmos/hygraph-schema";
import { CSSProperties, useMemo } from "react";
import { Box, BoxProps } from "@panda/jsx";
import { token } from "@panda/tokens";
import { Animate, OptimizedCosmosAssetFragment } from "..";
import { LottieAnimation, stretchLottieSetting } from "../lottie-animation";
import type { AnimationNames } from "../lottie-animation";
import { css, cx } from "@panda/css";
import { useAssetOptUrl } from "@components/hooks";
import { useCheckVideo } from "@components/hooks/asset.hooks";

export interface BackgroundContainerProps extends BoxProps {
	children?: React.ReactNode;
	className?: string;
	style?: CSSProperties;
	bgImageAsset?: OptimizedCosmosAssetFragment | null;
	bgBlur?: boolean;
	edsBackgroundColor?: CosmosEnumEdsBackgroundColors | null;
	bgAnimation?: AnimationNames | null;
	bgFullHeight?: boolean | null;
	bgContain?: boolean | null;
}

interface CssVariables extends CSSProperties {
	"--background-theme-color"?: string;
}

const boxStyles = css({
	position: "absolute",
	inset: "0",
});

const lottieStyles = css({
	"& svg": {
		position: "relative",
		width: "100%",
		maxWidth: "1920px",
		marginInline: "auto",
	},
});

export const BackgroundContainer = ({
	className = "",
	style,
	children,
	edsBackgroundColor,
	bgImageAsset,
	bgBlur,
	bgAnimation,
	bgFullHeight,
	bgContain,
	...rest
}: BackgroundContainerProps): React.ReactNode => {
	const optUrl = useAssetOptUrl(bgImageAsset);
	const isBgVideo = useCheckVideo(bgImageAsset?.mimeType);

	const bgStyle = useMemo(() => {
		if (!optUrl || isBgVideo) return "var(--background-theme-color)";

		if (bgFullHeight) {
			return `url(${optUrl}) no-repeat center / cover, linear-gradient(var(--background-theme-color), var(--background-theme-color))`;
		}

		if (bgContain) {
			return `url(${optUrl}) no-repeat top center / clamp(915px, 100%, 1200px) auto, linear-gradient(var(--background-theme-color), var(--background-theme-color))`;
		}

		return `linear-gradient(180deg, rgba(16, 16, 20, 0.00), var(--background-theme-color) 50%), url(${optUrl}) no-repeat top / cover, linear-gradient(var(--background-theme-color), var(--background-theme-color))`;
	}, [optUrl, isBgVideo, bgFullHeight, bgContain]);

	const themeStyle = useMemo(
		() =>
			({
				"--background-theme-color": token(
					`colors.eds.background.${edsBackgroundColor === CosmosEnumEdsBackgroundColors.ElevatedLowDefault ? "elevated.low.default" : "default"}`,
				),
			}) as CssVariables,

		[edsBackgroundColor],
	);

	if (!bgImageAsset && !edsBackgroundColor && !bgAnimation && !style) {
		return children;
	}

	return (
		<Box
			className={cx(
				"theme-container",
				edsBackgroundColor || "",
				bgImageAsset
					? css({
							"& .section-heading .cosmos-rich-text": {
								color: "eds.text.primary",
							},
						})
					: "",
				className || "",
			)}
			style={{
				background: bgStyle,
				position: bgBlur || bgAnimation ? "relative" : undefined,
				...themeStyle,
				...style,
			}}
			{...rest}
		>
			{bgAnimation ? (
				<Animate delay={0.35} type="fade">
					<LottieAnimation
						className={cx("lottie-container", boxStyles, lottieStyles)}
						lottie={bgAnimation}
						rendererSettings={stretchLottieSetting}
					/>
				</Animate>
			) : null}
			{bgBlur || bgAnimation ? (
				<Box
					className={cx(
						"theme-blur-container",
						boxStyles,
						bgAnimation
							? css({
									backdropFilter: "blur(100px)",
									"@supports (backdrop-filter: blur(min(8vw, 100px)))": {
										backdropFilter: "blur(min(8vw, 100px))",
									},
								})
							: css({ backdropFilter: "var(--effect-material-blur, blur(50px))" }),
					)}
				></Box>
			) : null}
			{children}
		</Box>
	);
};
